import {FormEvent, useEffect, useRef, useState} from 'react';
import type {DocumentCategory} from './fenzsafeStore';
import {createProfenzHandoffToken, profenzHandoffUrlForToken, type ProfenzHandoffContext} from './fenzsafeProfenzHandoff';
import {trackFenzSafeFunnelEvent} from './fenzsafeFunnelEvents';
import {authJourneyUrl} from './authReturnDestination';
import './FenzSafeShareView.css';
import {useBrowserFileDownload} from './useBrowserFileDownload';
import {FenzSafeViewDocumentButton} from './FenzSafeDocumentViewer';

/**
 * The real, live, unauthenticated page an attorney sees when opening a FenzSafe share link
 * (Issue #114 founder-delegated re-review, comment 5725255188, Slice C2). Deliberately separate
 * from FenzSafeAttorneyView.tsx, which is a walkthrough-only mock mounted from
 * FenzSafeWalkthrough.tsx and reads a matter straight out of the local synthetic/real store by
 * matterId -- that component is never reachable from a real share link and is left untouched.
 *
 * This component's only data source is the real, unauthenticated HTTP surface
 * AttorneyPackageController::view()/downloadDocument() serve: the frozen snapshot envelope (no
 * live matter/document read) and, per document, its exact original bytes through the token-scoped
 * download route. No provider object identifiers, storage keys, or internal metadata ever appear
 * in either response -- the wire shape below is exactly what the controller's serialize()/
 * serializeSnapshot() expose, nothing more.
 *
 * Non-enumeration (founder requirement): an unknown token, a revoked package, and a package with
 * no snapshot at all are all indistinguishable here -- every one of them renders the same "this
 * link isn't available" state, matching the backend's own non-enumerating 404.
 *
 * The "start a ProFenz service request" link (Issue #114 human-usability walkthrough finding)
 * reuses the same fenzsafeProfenzHandoff.ts contract FenzSafeAttorneyView.tsx's walkthrough-only
 * mock already builds -- AttorneyIntake.tsx already reads it (see Application.tsx's
 * decodeProfenzHandoffQuery() wiring), but until now nothing on the real, unauthenticated share
 * path an attorney actually lands on ever linked to it, leaving the receiving end unreachable from
 * the one real screen an attorney with only a share link can see. Built from this snapshot's own
 * public fields, never a synthetic Matter -- no internal matter id is available (or should be) to
 * an anonymous visitor, so `sourceMatterId`/`sourceOrderReference` both carry the human-readable
 * matter reference already shown above, matching how every other non-FenzSafe intake source already
 * treats this field (carolinaWorkflowStore.ts: "optional link, not tenant ownership").
 *
 * The "Create your own free ProFenz account" link (Issue #190's own named "professional viral
 * loop": consumer -> FenzSafe -> attorney summary/share -> attorney opens -> attorney engages/signs
 * up; Docket #192 item B4, per the dead-end this page had before) is the other half of that loop --
 * before this, the only link this page ever offered was the customer-side handoff above, leaving an
 * attorney who just reviewed a genuinely useful package with no path into becoming a ProFenz user
 * themselves. It reuses the existing generic `/sign-up` (customer) flow unchanged -- that form
 * already offers "Law firm" as one of its customer types -- rather than inventing a new signup
 * surface, matching this slice's own "smallest current dead ends only, no new future modules"
 * scope.
 */
type FenzSafeShareSnapshotDocument = {
  document_id: string;
  name: string;
  category: string;
  added_at: string;
};

type FenzSafeShareSnapshot = {
  matter_name: string;
  matter_reference: string;
  created_at: string;
  documents: FenzSafeShareSnapshotDocument[];
};

type FenzSafeSharePackageResponse = {
  attorney_package: {
    snapshot: FenzSafeShareSnapshot | null;
  };
};

/** Mirrors AttorneyPackageController::serializeEvidenceRequest()'s wire shape exactly. */
type FenzSafeShareEvidenceRequest = {
  request_id: string;
  description: string;
  status: 'pending' | 'fulfilled' | 'declined';
  created_at: string;
};

type FenzSafeShareViewState = 'loading' | 'not_found' | 'error' | 'ready';

export function FenzSafeShareView({token}: {token: string}) {
  const [state, setState] = useState<FenzSafeShareViewState>('loading');
  const [snapshot, setSnapshot] = useState<FenzSafeShareSnapshot | null>(null);
  const [downloadError, setDownloadError] = useState('');
  const {startDownload, downloadStatus} = useBrowserFileDownload(token);
  const [handoffBusy, setHandoffBusy] = useState(false);
  const [handoffError, setHandoffError] = useState('');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const inFlight = useRef<Set<string>>(new Set());
  const [requestItems, setRequestItems] = useState<string[]>(['']);
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [requestSentCount, setRequestSentCount] = useState<number | null>(null);
  // What the attorney has already asked for, and its current status -- fetched alongside the
  // main snapshot so the same page shows "what I asked for" next to "what I've been sent" (Issue
  // #114 founder direction, comment 5773888233 item 4: "missing-item tracking").
  const [priorRequests, setPriorRequests] = useState<FenzSafeShareEvidenceRequest[]>([]);

  async function refreshPriorRequests() {
    try {
      const response = await fetch(`/api/v1/fenzsafe/share/${token}/requests`, {method: 'GET', credentials: 'omit', headers: {Accept: 'application/json'}});
      if (!response.ok) return;
      const body = (await response.json()) as {evidence_requests?: FenzSafeShareEvidenceRequest[]};
      setPriorRequests(body.evidence_requests ?? []);
    } catch {
      // Best-effort -- the rest of the page (documents, the request form) works fine without this.
    }
  }

  // Non-indexable, non-cacheable by search engines -- this is a private capability URL, not
  // public content. Client-side is the only layer this SPA route can control directly; the
  // underlying API responses already send Cache-Control: no-store (ApiResponse::json()).
  useEffect(() => {
    document.title = 'Shared documents';
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      document.head.appendChild(meta);
    }
    const previous = meta.getAttribute('content');
    meta.setAttribute('content', 'noindex, nofollow');
    return () => {
      if (previous === null) meta?.remove();
      else meta?.setAttribute('content', previous);
    };
  }, []);

  useEffect(() => {
    let active = true;
    setState('loading');
    // A failed fetch() itself (offline, DNS, connection reset) means nothing about the token --
    // it's a transient local/network condition, not a security-relevant fact about this share.
    // Collapsing it into the same "this link isn't available" wording as a real revoked/unknown
    // token would be actively misleading (there is nothing wrong with the link) and offers no way
    // to recover short of a full reload. Only a completed HTTP response (ok or not) reflects the
    // token's actual state and is safe to fold into the non-enumerating "not_found" wording below.
    fetch(`/api/v1/fenzsafe/share/${token}`, {method: 'GET', credentials: 'omit', headers: {Accept: 'application/json'}})
      .then(async (response) => {
        if (!response.ok) {
          if (active) setState('not_found');
          return;
        }
        const resolvedSnapshot = ((await response.json()) as FenzSafeSharePackageResponse).attorney_package.snapshot;
        if (!active) return;
        if (resolvedSnapshot === null) {
          setState('not_found');
          return;
        }
        setSnapshot(resolvedSnapshot);
        setState('ready');
        void refreshPriorRequests();
        // The one real, unauthenticated "attorney opened the share" signal (Issue #164 founder
        // continuation directive, P2P item 1) -- fires once per successful load of a genuine,
        // non-revoked snapshot, distinct from the durable, tenant-scoped
        // fenzsafe_attorney_package.share_link_viewed audit event this same view already causes
        // server-side (docs/ai/FENZSAFE_MEASUREMENT_CONTRACT.md §3), which remains the durable
        // source of truth for view volume. This client-side event exists only for real-time GA4
        // funnel visibility and carries no matter/document/party identifying information.
        trackFenzSafeFunnelEvent({milestone: 'attorney_opened'});
      })
      .catch(() => {
        if (active) setState('error');
      });
    return () => {
      active = false;
    };
  }, [token, retryCount]);

  async function downloadDocument(documentId: string, name: string) {
    // Belt-and-suspenders against a same-document double-invocation landing inside the brief
    // window before React commits the disabled attribute from the first click's setDownloadingId
    // (real human click speed can't hit it, but a synthetic double-fire could) -- a Set survives
    // across renders without waiting on state to commit first.
    if (inFlight.current.has(documentId)) return;
    inFlight.current.add(documentId);
    setDownloadError('');
    setDownloadingId(documentId);
    try {
      const response = await fetch(`/api/v1/fenzsafe/share/${token}/documents/${documentId}/bytes`, {method: 'GET', credentials: 'omit'});
      if (!response.ok) {
        throw new Error('download_failed');
      }
      const blob = await response.blob();
      startDownload(blob, name, `/api/v1/fenzsafe/share/${token}/documents/${documentId}/bytes`);
    } catch {
      // Names the specific document -- with more than one shared file, a generic "this document
      // could not be downloaded" leaves no way to tell which one failed once the visitor has moved
      // on to another, especially since the button that triggered it doesn't stay in any failed
      // visual state of its own.
      setDownloadError(`"${name}" could not be downloaded. It may no longer be available -- ask the sender for a new link.`);
    } finally {
      inFlight.current.delete(documentId);
      setDownloadingId(null);
    }
  }

  /**
   * The one place an attorney holding a share link can write anything back into the client's
   * matter (Issue #164 founder direction, 2026-09-22: "the attorney could review and state, I
   * also need the text messages you stated you had ... the request goes back to the client").
   * Each non-blank line becomes its own requested item -- one row, not a paragraph the client
   * has to parse, matching FenzSafeEvidenceRequest's own "one row per requested item" model.
   */
  async function submitRequests(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const descriptions = requestItems.map((item) => item.trim()).filter((item) => item !== '');
    if (descriptions.length === 0) return;
    setRequestSubmitting(true);
    setRequestError('');
    try {
      const response = await fetch(`/api/v1/fenzsafe/share/${token}/requests`, {
        method: 'POST',
        credentials: 'omit',
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({descriptions}),
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as {message?: string} | null;
        throw new Error(body?.message ?? 'request_failed');
      }
      setRequestItems(['']);
      setRequestSentCount(descriptions.length);
      void refreshPriorRequests();
    } catch (error) {
      setRequestError(error instanceof Error && error.message !== 'request_failed' ? error.message : 'That request could not be sent. Check your connection and try again.');
    } finally {
      setRequestSubmitting(false);
    }
  }

  function updateRequestItem(index: number, value: string) {
    setRequestSentCount(null);
    setRequestItems((items) => items.map((item, itemIndex) => (itemIndex === index ? value : item)));
  }

  function removeRequestItem(index: number) {
    setRequestItems((items) => (items.length === 1 ? [''] : items.filter((_, itemIndex) => itemIndex !== index)));
  }

  if (state === 'loading') {
    return (
      <section aria-label="Shared FenzSafe package" className="fsv-root">
        <p className="fsv-status" role="status">
          Loading shared documents…
        </p>
      </section>
    );
  }

  if (state === 'error') {
    return (
      <section aria-label="Shared FenzSafe package could not be loaded" className="fsv-root">
        <div className="fsv-not-found">
          <h1>Something went wrong loading this page</h1>
          <p role="alert">This looks like a connection problem, not a problem with the link itself. Check your connection and try again.</p>
          <button type="button" className="fsv-download-button" onClick={() => setRetryCount((count) => count + 1)}>
            Try again
          </button>
        </div>
      </section>
    );
  }

  if (state === 'not_found' || snapshot === null) {
    return (
      <section aria-label="Shared FenzSafe package not available" className="fsv-root">
        <div className="fsv-not-found">
          <h1>This link isn&rsquo;t available</h1>
          <p>It may have been revoked, or the sender hasn&rsquo;t shared anything here yet. Ask them to send a new link if you still need these documents.</p>
        </div>
      </section>
    );
  }

  // A lightweight "Matter Summary" -- how many documents, broken down by category -- computed
  // entirely from data this page already has, no new backend concept (Issue #114 founder
  // direction, comment 5773888233 item 2: "upgrade the real attorney share link from a download
  // page to a useful Matter Summary"). Deliberately does not claim key dates, key people, or
  // "gaps" -- those would require case-type-aware expected-document knowledge this product
  // doesn't have yet, and inventing one here would be exactly the kind of unsupported claim this
  // session's own standing rule against fabricated legal/factual content exists to prevent.
  const categoryCounts = new Map<string, number>();
  for (const doc of snapshot.documents) {
    categoryCounts.set(doc.category, (categoryCounts.get(doc.category) ?? 0) + 1);
  }
  const pendingRequestCount = priorRequests.filter((item) => item.status === 'pending').length;

  const handoffContext: ProfenzHandoffContext = {
    sourceMatterId: snapshot.matter_reference,
    caption: snapshot.matter_name,
    sourceOrderReference: snapshot.matter_reference,
    documents: snapshot.documents.map((doc) => ({name: doc.name, category: doc.category as DocumentCategory})),
  };

  // R9 correction (Issue #192 comment `5803848964`): the matter caption/reference/document
  // details above must never reach a browser-visible URL. Exchange them for an opaque,
  // single-use, short-lived server token first, then navigate with only that token in the query
  // string -- never a plain <a href> built from handoffContext directly.
  async function startProfenzHandoff() {
    if (handoffBusy) return;
    setHandoffBusy(true);
    setHandoffError('');
    trackFenzSafeFunnelEvent({milestone: 'profenz_transaction'});
    const token = await createProfenzHandoffToken(handoffContext);
    if (token === null) {
      setHandoffError('This could not be started right now. Try again in a moment.');
      setHandoffBusy(false);
      return;
    }
    window.location.assign(profenzHandoffUrlForToken(token));
  }

  return (
    <section aria-label="Shared FenzSafe package" className="fsv-root">
      <div className="fsv-cover">
        <div className="fsv-brand" aria-hidden="true">
          FenzSafe
        </div>
        <h1>{snapshot.matter_name}</h1>
        <p className="fsv-meta">
          Reference {snapshot.matter_reference} · Shared {snapshot.created_at}
        </p>
        {snapshot.documents.length > 0 && (
          <ul className="fsv-summary-chips" aria-label="Documents by category">
            {Array.from(categoryCounts.entries()).map(([category, count]) => (
              <li key={category} className="fsv-summary-chip">
                {category} ({count})
              </li>
            ))}
          </ul>
        )}
        {pendingRequestCount > 0 && (
          <p className="fsv-meta" role="status">
            {pendingRequestCount === 1 ? 'One item you requested is' : `${pendingRequestCount} items you requested are`} still outstanding.
          </p>
        )}
        {snapshot.documents.length > 0 && (
          <p className="fsv-handoff">
            {/* A button, not an <a href>, because the real target isn't known until the opaque
                handoff token exists server-side (see startProfenzHandoff() above). Funnel tracking
                fires on click intent, same as before this correction; the durable, authoritative
                signal for a completed conversion remains the existing fenzsafe_source_matter_id
                -tagged attorney.order_draft_details_saved audit event (docs/ai/
                FENZSAFE_MEASUREMENT_CONTRACT.md §3, KPI #9), unchanged by this event. */}
            <button type="button" className="button-link" disabled={handoffBusy} onClick={() => { void startProfenzHandoff(); }}>
              {handoffBusy ? 'Starting…' : 'Start a ProFenz service request from these documents →'}
            </button>
            {handoffError && <span role="alert" className="fsv-error"> {handoffError}</span>}
          </p>
        )}
      </div>
      <section className="fsv-section" aria-labelledby="fsv-documents-heading">
        <h2 id="fsv-documents-heading">Documents ({snapshot.documents.length})</h2>
        {downloadStatus}
        {downloadError && (
          <p className="fsv-error" role="alert">
            {downloadError}
          </p>
        )}
        {snapshot.documents.length === 0 ? (
          <p className="fsv-status" role="status">
            No documents have been shared here yet.
          </p>
        ) : (
          <ul className="fsv-doc-list">
            {snapshot.documents.map((doc) => (
              <li key={doc.document_id} className="fsv-doc">
                <div className="fsv-doc-info">
                  <p className="fsv-doc-name">{doc.name}</p>
                  <p className="fsv-doc-meta">
                    {doc.category} · Added {doc.added_at}
                  </p>
                </div>
                <FenzSafeViewDocumentButton key={`${token}:${doc.document_id}`} name={doc.name} className="fsv-download-button"
                  loadFile={async signal => {
                    const response = await fetch(`/api/v1/fenzsafe/share/${token}/documents/${doc.document_id}/bytes`, {method: 'GET', credentials: 'omit', signal});
                    if (!response.ok) throw new Error('document_unavailable');
                    return response.blob();
                  }} />
                <button
                  type="button"
                  className="fsv-download-button"
                  onClick={() => downloadDocument(doc.document_id, doc.name)}
                  disabled={downloadingId === doc.document_id}
                  aria-label={downloadingId === doc.document_id ? `Downloading ${doc.name}…` : `Download ${doc.name}`}
                >
                  {downloadingId === doc.document_id ? 'Downloading…' : 'Download'}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
      {priorRequests.length > 0 && (
        <section className="fsv-section" aria-labelledby="fsv-prior-requests-heading">
          <h2 id="fsv-prior-requests-heading">Your requests</h2>
          <ul className="fsv-prior-request-list">
            {priorRequests.map((item) => (
              <li key={item.request_id} className="fsv-prior-request">
                <span>{item.description}</span>
                <span className={`fsv-prior-request-status fsv-prior-request-status-${item.status}`}>
                  {item.status === 'pending' ? 'Outstanding' : item.status === 'fulfilled' ? 'Provided' : "Client doesn't have this"}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
      <section className="fsv-section" aria-labelledby="fsv-requests-heading">
        <h2 id="fsv-requests-heading">Need something else from your client?</h2>
        <p className="fsv-status">List what you still need, in plain language -- e.g. "Photos from the September 4th incident" or "The last three months of bank statements." Your client will see this the next time they open FenzSafe.</p>
        {requestSentCount !== null && (
          <p className="fsv-status" role="status">
            Sent -- your client will see {requestSentCount === 1 ? 'the item' : `all ${requestSentCount} items`} you asked for.
          </p>
        )}
        {requestError && (
          <p className="fsv-error" role="alert">
            {requestError}
          </p>
        )}
        <form className="fsv-request-form" onSubmit={submitRequests}>
          {requestItems.map((item, index) => (
            <div className="fsv-request-row" key={index}>
              <label className="fsv-visually-hidden" htmlFor={`fsv-request-item-${index}`}>
                Requested item {index + 1}
              </label>
              <input
                id={`fsv-request-item-${index}`}
                type="text"
                value={item}
                maxLength={240}
                placeholder="e.g. Video from September 3rd"
                onChange={(event) => updateRequestItem(index, event.target.value)}
              />
              {requestItems.length > 1 && (
                <button type="button" className="fsv-request-remove" onClick={() => removeRequestItem(index)} aria-label={`Remove requested item ${index + 1}`}>
                  Remove
                </button>
              )}
            </div>
          ))}
          <div className="fsv-request-actions">
            <button type="button" className="fsv-download-button" onClick={() => setRequestItems((items) => [...items, ''])} disabled={requestItems.length >= 10}>
              + Add another item
            </button>
            <button type="submit" className="fsv-request-submit" disabled={requestSubmitting || requestItems.every((item) => item.trim() === '')}>
              {requestSubmitting ? 'Sending…' : 'Send request'}
            </button>
          </div>
        </form>
      </section>
      <section className="fsv-section" aria-labelledby="fsv-attorney-signup-heading">
        <h2 id="fsv-attorney-signup-heading">Are you an attorney or paralegal?</h2>
        <p className="fsv-status">Create your own free ProFenz account to manage cases like this one, request evidence from clients, and share packages with your own firm.</p>
        <a className="button-link" href={authJourneyUrl('/sign-up')} onClick={() => trackFenzSafeFunnelEvent({milestone: 'attorney_signup_clicked'})}>
          Create your own free ProFenz account →
        </a>
      </section>
    </section>
  );
}
